import React from "react";
import PropTypes from "prop-types";
import {FaSolidIcon} from "../../../../../FontAwesomeIcons";
import './AudioPlayer.css';
import './AudioPlayerMine.css';
import './AudioPlayerYours.css';

export class AudioPlayer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isPlaying: false,
            duration: null,
            currentTime: 0,
            canPlay: false,
        };
        this.audio = null;
        this.hasLoadedSource = false;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.src !== this.props.src) {
            this.resetAudio();
        }
    }

    componentWillUnmount() {
        this.resetAudio(false);
    }

    handleAudioCanPlay = () => {
        this.setState({canPlay: true});
    }

    handleAudioLoadedMetadata = () => {
        if (!this.audio) return;
        if (this.audio.duration === Infinity || isNaN(Number(this.audio.duration))) {
            this.audio.currentTime = 1e101;
            this.audio.addEventListener('timeupdate', this.resolveDurationFromTimeUpdate);
        } else {
            this.setState({duration: this.audio.duration});
        }
    }

    handleAudioEnded = () => {
        this.setState({currentTime: 0, isPlaying: false});
        if (this.audio) {
            this.audio.currentTime = 0;
        }
    }

    handleAudioTimeUpdate = () => {
        if (!this.audio) return;
        this.setState({currentTime: this.audio.currentTime});
    }

    resolveDurationFromTimeUpdate = () => {
        if (!this.audio) return;
        this.audio.removeEventListener('timeupdate', this.resolveDurationFromTimeUpdate);
        this.audio.currentTime = 0;
        this.setState({duration: this.audio.duration});
    }

    initAudio = () => {
        if (this.audio) return;
        this.audio = new Audio();
        this.audio.preload = 'metadata';
        this.audio.src = this.props.src;

        this.audio.addEventListener('canplaythrough', this.handleAudioCanPlay);
        this.audio.addEventListener('loadedmetadata', this.handleAudioLoadedMetadata);
        this.audio.addEventListener('ended', this.handleAudioEnded);
        this.audio.addEventListener('timeupdate', this.handleAudioTimeUpdate);
    }

    ensureAudio = () => {
        if (!this.audio) {
            this.initAudio();
        }
        return this.audio;
    }

    ensureAudioLoaded = () => {
        const audio = this.ensureAudio();
        if (audio && !this.hasLoadedSource) {
            audio.load();
            this.hasLoadedSource = true;
        }
        return audio;
    }

    resetAudio = (resetState = true) => {
        if (this.audio) {
            this.audio.pause();
            this.audio.removeEventListener('canplaythrough', this.handleAudioCanPlay);
            this.audio.removeEventListener('loadedmetadata', this.handleAudioLoadedMetadata);
            this.audio.removeEventListener('ended', this.handleAudioEnded);
            this.audio.removeEventListener('timeupdate', this.handleAudioTimeUpdate);
            this.audio.removeEventListener('timeupdate', this.resolveDurationFromTimeUpdate);
            this.audio = null;
        }
        this.hasLoadedSource = false;
        if (resetState) {
            this.setState({
                isPlaying: false,
                duration: null,
                currentTime: 0,
                canPlay: false,
            });
        }
    }

    isAudioReady = () => this.state.canPlay && this.state.duration !== Infinity && this.state.duration;

    handlePlay = _ => {
        const audio = this.ensureAudioLoaded();
        if (!audio) return;
        this.setState({isPlaying: true}, _ => {
            const playPromise = audio.play();
            if (playPromise?.catch) {
                playPromise.catch(() => {
                    this.setState({isPlaying: false});
                });
            }
        });
    }

    handlePause = _ => {
        if (!this.audio) return;
        this.audio.pause();
        this.setState({isPlaying: false});
    }

    handleSliderChange = ({target}) => {
        const audio = this.ensureAudioLoaded();
        if (!audio || !this.state.duration) return;
        const selectedTime = this.state.duration * (target.value / 100);
        this.setState({currentTime: selectedTime}, _ => {
            audio.currentTime = selectedTime;
        });
    }

    render() {

        const progressPerc = this.state.duration ? this.state.currentTime * (100 / this.state.duration) : 0;

        return (
            <React.Fragment>
                <div className={"d-flex align-items-center audio-player"}>
                    {
                        this.isAudioReady() ?
                            <React.Fragment>
                                {
                                    this.state.isPlaying ?
                                        <div className={'audio-control'} onClick={this.handlePause}>
                                            <FaSolidIcon name={"pause"}/>
                                        </div>
                                        :
                                        <div className={'audio-control'} onClick={this.handlePlay}>
                                            <FaSolidIcon name={"play"}/>
                                        </div>
                                }
                            </React.Fragment>
                            :
                            <div className={'audio-control'} onClick={this.handlePlay}>
                                <FaSolidIcon name={"play"}/>
                            </div>
                    }
                    <div className={"ml-2"}>
                        <div>
                            <input className={'audio-control'} type={"range"} min={0} max={100} value={progressPerc}
                                   disabled={!this.isAudioReady()} onChange={this.handleSliderChange}/>
                        </div>
                        <div className={"small"}>
                            {getDurationString(this.state.currentTime > 0 ? this.state.currentTime : (this.state.duration || 0))}
                        </div>
                    </div>
                </div>
            </React.Fragment>
        )
    }

}

AudioPlayer.propTypes = {
    src: PropTypes.string,
    type: PropTypes.string
}


function getDurationString(duration) {
    const durationDate = new Date(duration * 1000).toISOString();
    return duration < 3600 ? durationDate.substring(14, 19) : durationDate.substring(11, 16);
}
